import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CustomIcons } from '@shared/models/custom-icons';
import { SelectModelImpaired } from '@impairedShared/select-impaired/models';

@Component({
  selector: 'app-impaired-pagination',
  templateUrl: './impaired-pagination.component.html',
  styleUrls: ['./impaired-pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpairedPaginationComponent implements OnInit, OnDestroy {
  @Input() public set allCount(count: number) {
    this._allCount = count;
    const pageSize = +(this.route.snapshot.queryParams['pageSize'] ?? 10);
    const pageIndex = +(this.route.snapshot.queryParams['pageIndex'] ?? 0) + 1;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.pageIndexControl.setValue(pageIndex, { emitEvent: false });
  }
  get allCount(): number {
    return this._allCount;
  }
  private _allCount = 0;

  @Output() public readonly paginatorChanged: EventEmitter<{
    pageIndex: number;
    pageSize: number;
  }> = new EventEmitter();

  public icons = CustomIcons;
  public pageSize = 10;
  public pageSizes = [10, 20, 50, 100];
  public pageIndex: number | null = 1;
  public prevPageIndex = 1;
  public pageIndexControl = new FormControl<number>(this.pageIndex || 1, [Validators.min(1)]);

  private componentDestroy = new Subject<void>();

  public get pageSizeOption(): SelectModelImpaired {
    return {
      value: this.pageSize,
      title: this.pageSize,
      titleEn: this.pageSize,
      titleRu: this.pageSize
    };
  }

  public get pageSizeOptions(): SelectModelImpaired[] {
    return this.pageSizes.map(size => ({
      value: size,
      title: size,
      titleEn: size,
      titleRu: size
    }));
  }

  public get nextPageAccepted() {
    return this.allCount > this.pageSize * this.pageIndex!;
  }

  public get prevPageAccepted() {
    return this.pageIndex! > 1;
  }

  public get pageSizeView() {
    return Math.min(this.allCount, this.pageSize);
  }

  constructor(private route: ActivatedRoute) {}

  ngOnInit() {
    this.pageIndexControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.componentDestroy))
      .subscribe((pageIndex) => {
        this.pageIndex = pageIndex;
      });
  }

  ngOnDestroy() {
    this.componentDestroy.next();
    this.componentDestroy.complete();
  }

  private paginatorChange(): void {
    this.paginatorChanged.emit({
      pageSize: this.pageSize,
      pageIndex: this.pageIndex!
    });
  }

  public pageIndexChange(hasPaginatorChange = true): void {
    if (!this.pageIndex) {
      this.setPageIndex(this.prevPageIndex);
      return;
    }

    let lastPage = this.allCount / this.pageSize;
    if (Math.trunc(lastPage) < lastPage) {
      lastPage = Math.trunc(lastPage) + 1;
    } else if (lastPage === 0) {
      lastPage = 1;
    }
    if (!this.pageIndex) {
      this.pageIndex = 1;
    } else if (this.pageIndex >= lastPage) {
      this.pageIndex = lastPage;
    }

    if (hasPaginatorChange) {
      this.paginatorChange();
    }
  }

  public numberOnly($event: KeyboardEvent): boolean {
    const charCode = $event.which ? $event.which : $event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  public setPageSize(newPageSize: SelectModelImpaired): void {
    if (newPageSize.value) {
      const pageSize = +newPageSize.value;
      if (!isNaN(pageSize)) {
        const newMaxPageIndex = Math.ceil(this.allCount / pageSize);

        if (newMaxPageIndex < this.pageIndex!) {
          this.pageIndex = newMaxPageIndex;
        }

        this.pageSize = pageSize;
        this.paginatorChange();
      }
    }
  }

  toFirstPage() {
    this.setPageIndex(1);
    this.paginatorChange();
  }

  toPrevPage() {
    this.setPageIndex(this.pageIndex! - 1);
    this.paginatorChange();
  }

  toNextPage() {
    this.setPageIndex(this.pageIndex! + 1);
    this.paginatorChange();
  }

  toLastPage() {
    this.setPageIndex(Math.ceil(this.allCount / this.pageSize));
    this.paginatorChange();
  }

  setPageIndex(pageIndex: number) {
    this.pageIndex = pageIndex;
    this.pageIndexControl.setValue(pageIndex);
    this.prevPageIndex = pageIndex;
  }
}
